// Marketplace API for DIOS10
import apiClient from './client';
import { API_ENDPOINTS } from './config';

export const marketplaceApi = {
    getSettings: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.SETTINGS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getBalances: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.BALANCES);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Auctions
    createAuction: async (auctionData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.CREATE_AUCTION, auctionData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getAuctions: async (filters = {}) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_AUCTIONS, {
                params: filters
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getAuctionsByTemplate: async (templateId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_AUCTIONS_BY_TEMPLATE(templateId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    placeBid: async (auctionId, bidData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.PLACE_BID(auctionId), bidData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Sales
    createSale: async (saleData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.CREATE_SALE, saleData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getSales: async (filters = {}) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_SALES, {
                params: filters
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getSalesByTemplate: async (templateId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_SALES_BY_TEMPLATE(templateId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getUserListedSales: async (userId, limit = 50) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_USER_LISTED_SALES(userId), {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getUserSoldSales: async (userId, limit = 50) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_USER_SOLD_SALES(userId), {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    cancelSale: async (saleId, userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.CANCEL_SALE(saleId), { userId });
            return response;
        } catch (error) {
            throw error;
        }
    },

    cancelSaleByCardId: async (cardId, userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.CANCEL_SALE_BY_CARD(cardId), { userId });
            return response;
        } catch (error) {
            throw error;
        }
    },

    purchase: async (saleId, purchaseData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.PURCHASE(saleId), purchaseData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Loans
    getLoans: async (filters = {}) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_LOANS, {
                params: filters
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getLoansByTemplate: async (templateId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_LOANS_BY_TEMPLATE(templateId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    createLoan: async (loanData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.CREATE_LOAN, loanData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    acceptLoan: async (loanId, acceptData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.ACCEPT_LOAN(loanId), acceptData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getUserLoans: async (userId, role = 'lender', limit = 50) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_USER_LOANS(userId), {
                params: { role, limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getUserListedLoans: async (userId, limit = 50) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_USER_LISTED_LOANS(userId), {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    cancelLoan: async (loanId, userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.CANCEL_LOAN(loanId), { userId });
            return response;
        } catch (error) {
            throw error;
        }
    },

    updatePeriodLoanPrice: async (loanId, userId, pricePerPeriod) => {
        try {
            const response = await apiClient.patch(
                API_ENDPOINTS.MARKETPLACE.UPDATE_PERIOD_LOAN_PRICE(loanId),
                { userId, pricePerPeriod }
            );
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Trades
    createTrade: async (tradeData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.CREATE_TRADE, tradeData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getTrade: async (tradeId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_TRADE(tradeId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getUserTrades: async (userId, limit = 50) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_USER_TRADES(userId), {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getOpenTrades: async (limit = 50) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_OPEN_TRADES, {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getOpenTradesForUser: async (userId, limit = 50) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_OPEN_TRADES_FOR_USER(userId), {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getUserCreatedOpenTrades: async (userId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_USER_CREATED_OPEN_TRADES(userId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    acceptTrade: async (tradeId, userId, card2Id) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.ACCEPT_TRADE(tradeId), { 
                userId,
                card2Id 
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    rejectTrade: async (tradeId, userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.REJECT_TRADE(tradeId), { userId });
            return response;
        } catch (error) {
            throw error;
        }
    },

    cancelTrade: async (tradeId, userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.CANCEL_TRADE(tradeId), { userId });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Counter Offers
    createCounterOffer: async (tradeId, userId, cardId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.CREATE_COUNTER_OFFER(tradeId), {
                userId,
                cardId
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getCounterOffersForTrade: async (tradeId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_COUNTER_OFFERS_FOR_TRADE(tradeId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getCounterOffersForUser: async (userId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_COUNTER_OFFERS_FOR_USER(userId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    acceptCounterOffer: async (counterOfferId, userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.ACCEPT_COUNTER_OFFER(counterOfferId), {
                userId
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    rejectCounterOffer: async (counterOfferId, userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.REJECT_COUNTER_OFFER(counterOfferId), {
                userId
            });
            return response;
        } catch (error) {
            throw error;
        }
    },
    cancelCounterOffer: async (counterOfferId, userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.CANCEL_COUNTER_OFFER(counterOfferId), {
                userId
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Packs
    getPacks: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_PACKS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    openPack: async (packId, openData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.OPEN_PACK(packId), openData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getPackHistory: async (userId, limit = 50) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.MARKETPLACE.GET_PACK_HISTORY(userId), {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Open starter pack (free, 30 basic cards)
    openStarterPack: async (userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.MARKETPLACE.OPEN_STARTER_PACK, {
                userId
            });
            return response;
        } catch (error) {
            throw error;
        }
    }
};

export default marketplaceApi;
